<template>
    <v-content>
        <div class="container">
            <div class="title">
                <span>Drag and drop to change order</span>
            </div>
            <div>
                <div class="reference-filter-container" v-if="doesSelectedContentTypeHaveReferences">
                    <reference-filter :mode="referenceFilterMode"></reference-filter>
                </div>
                <div v-if="showOrderingArea">
                    <div class="table">
                        <draggable v-model="componentEntries" :force-fallback="true">
                            <div v-for="entry in mappedEntries" :key="entry.id">
                                <div>
                                    <div class="entry" :class="entry.extraClasses">
                                        <span>{{entry.name}}</span>
                                    </div>
                                </div>
                            </div>
                        </draggable>
                    </div>
                    <div>
                        <v-btn color="info" @click="onSaveOrderClick()">
                            <span v-if="isOrderUpdating">Saving...</span>
                            <span v-else class="capitalize-text">Save Order</span>
                        </v-btn>
                    </div>
                </div>
            </div>
        </div>
        <v-snackbar
                :timeout="snackBar.timeout"
                :bottom="true"
                v-model="snackBar.isShowing"
                :color="snackBar.color">
            {{ snackBar.text }}
            <v-btn dark flat @click.native="snackBar.isShowing = false">Ok</v-btn>
        </v-snackbar>
    </v-content>
</template>

<script>

    import lodash from '../services/lodash'
    import helpers from '../services/helpers'

    import draggable from 'vuedraggable'

    import {
        LOADING_PRISTINE,
        LOADING_STARTED,
        LOADING_COMPLETED_SUCCESS,
        LOADING_COMPLETED_FAILURE,
        SET_CONTENT_DASHBOARD_STATE_AND_RELOAD,
        UPDATE_ARRAY_OF_ENTRIES,
        SET_REORDER_CONTENT_BACKING_ARRAY,
        SET_REORDER_CONTENT_BACKING_ARRAY_AND_SORT,
        GET_FILTERED_ENTRIES_REORDER_CONTENT,
        MODE_REFERENCE_FILTER_REORDER,
        SET_REORDER_CONTENT_IDENTIFIER,
        SNACKBAR_COLOR_SUCCESS,
        SNACKBAR_COLOR_ERROR,
        SNACKBAR_COLOR_INFO,
    } from "../services/constants"

    export default {
        name: 'reorder-content',
        components: {
            draggable,
        },
        data: function () {
            return {
                updateState: {
                    state: LOADING_PRISTINE,
                    error: null,
                },
                snackBar: {
                    isShowing: false,
                    text: '',
                    color: SNACKBAR_COLOR_INFO,
                    timeout: 6000
                }
            }
        },
        computed: {
            componentEntries: {
                get() {
                    const a = this.$store.getters[GET_FILTERED_ENTRIES_REORDER_CONTENT]
                    // console.log(a)
                    return a
                },
                set(newValue) {
                    this.$store.commit(SET_REORDER_CONTENT_BACKING_ARRAY, newValue)
                },
            },
            isSpaceMetadataLoaded() {
                return !!this.$store.state.spaceMetadata
            },
            isOrderUpdating() {
                return this.updateState.state === LOADING_STARTED
            },
            updateResultText() {
                let str = ''
                if (this.updateState.state === LOADING_COMPLETED_SUCCESS) {
                    str += 'Order Updated'
                } else if (this.updateState.state === LOADING_COMPLETED_FAILURE) {
                    str += this.updateState.error
                }
                return str
            },
            spaceContentTypes() {
                return this.$store.state.spaceContentTypes
            },
            spaceContentIdentifier() {
                return this.$route.params.contentTypeIdentifier
            },
            selectedContentType() {
                if (this.spaceContentTypes) {
                    return this.spaceContentTypes.find(contentType => {
                        return contentType.identifier === this.spaceContentIdentifier
                    });
                } else {
                    return null
                }
            },
            doesSelectedContentTypeHaveReferences() {
                if (this.selectedContentType) {
                    return this.selectedContentType.fields.some(helpers.isFieldANonAssetReferenceField)
                }
                return false
            },
            entries() {
                const entries = this.$store.state.spaceContent[this.spaceContentIdentifier]
                return entries ? entries : []
            },
            mappedEntries: function () {
                if (this.componentEntries) {
                    return this.componentEntries.map((entry, index, arr) => {
                        return {
                            id: entry.id,
                            name: this.getPrimaryDisplayName(entry),
                            originalEntry: entry,
                            extraClasses: index === arr.length - 1 ? 'last-entry' : 'regular-entry',
                            // sortOrder: this.findOrderedField(entry).value
                        }
                    })
                }
                return []
            },
            showOrderingArea() {
                return this.mappedEntries.length > 0
            },
            referenceFilterMode() {
                return MODE_REFERENCE_FILTER_REORDER
            },
        },
        watch: {
            isSpaceMetadataLoaded(newVal, oldVal) {
                if (newVal && !oldVal) {
                    this.init()
                }
            },
        },
        methods: {
            init() {
                this.$store.commit(SET_REORDER_CONTENT_IDENTIFIER, this.spaceContentIdentifier)
                if (this.entries && this.entries.length > 0) {
                    this.setComponentEntries()
                } else {
                    if (this.isSpaceMetadataLoaded) {
                        this.$store.dispatch(SET_CONTENT_DASHBOARD_STATE_AND_RELOAD, {
                            selectedContentType: this.spaceContentIdentifier,
                        }).then(() => {
                            this.setComponentEntries()
                        });
                    }
                }
            },
            setComponentEntries() {
                this.$store.commit(SET_REORDER_CONTENT_BACKING_ARRAY_AND_SORT, lodash.cloneDeep(this.entries))
            },
            getPrimaryDisplayName: helpers.getPrimaryDisplayName,
            orderedEntrySort(entryA, entryB) {
                return helpers.orderedEntrySort(entryA, entryB)
            },
            onSaveOrderClick() {
                const updatedEntries = this.componentEntries.map((entry, index) => {
                    return this.updateSingleEntryOrder(entry, index + 1)
                })
                // console.log(updatedEntries)
                this.updateState.state = LOADING_STARTED
                this.$store.dispatch(UPDATE_ARRAY_OF_ENTRIES, updatedEntries).then(() => {
                    this.updateState.state = LOADING_COMPLETED_SUCCESS
                    this.showSnackBar(`Content successfully reordered.`, LOADING_COMPLETED_SUCCESS)
                    this.init()
                }).catch(err => {
                    this.updateState.state = LOADING_COMPLETED_SUCCESS
                    this.updateState.error = err
                })

            },
            findOrderedFieldDefinition(entry) {
                return helpers.findOrderedFieldDefinition(entry)
            },
            findOrderedField(entry) {
                return helpers.findOrderedField(entry)
            },
            updateSingleEntryOrder(entry, index) {
                const clonedEntry = lodash.cloneDeep(entry)
                const orderFieldOrNull = this.findOrderedField(clonedEntry)
                if (orderFieldOrNull) {
                    orderFieldOrNull.value = index
                } else {
                    const fieldOfContentTypeOfEntry = this.findOrderedFieldDefinition(entry)
                    if (fieldOfContentTypeOfEntry) {
                        clonedEntry.fields.push({
                            identifier: fieldOfContentTypeOfEntry.identifier,
                            value: index,
                            type: fieldOfContentTypeOfEntry.type,
                            allowsMultiple: fieldOfContentTypeOfEntry.allowsMultiple
                        })
                    }
                }

                return clonedEntry

            },
            showSnackBar: function (text, status) {
                this.snackBar.text = text
                switch (status) {
                    case LOADING_COMPLETED_SUCCESS:
                        this.snackBar.color = SNACKBAR_COLOR_SUCCESS
                        break
                    case LOADING_COMPLETED_FAILURE:
                        this.snackBar.color = SNACKBAR_COLOR_ERROR
                        break
                    default:
                        this.snackBar.color = SNACKBAR_COLOR_INFO
                        break
                }
                this.snackBar.isShowing = true
            },
        },
        created: function () {
            this.init()
        }
    }
</script>

<style scoped lang="scss">
    @import "../meta";

    .title {
        font-size: 21px;
        margin: 0 0 10px 0;
    }

    .table {
        border-radius: 2px;
        box-shadow: 0px 2px 1px -1px rgba(0, 0, 0, 0.2), 0px 1px 1px 0px rgba(0, 0, 0, 0.14), 0px 1px 3px 0px rgba(0, 0, 0, 0.12) !important;
        cursor: pointer;
    }

    .entry {
        padding: 13px;
        background-color: #ffffff;
        font-size: 13px;
        font-weight: 400;

        &.regular-entry {
            border-bottom: 1px solid rgba(0, 0, 0, 0.12);
        }
    }

</style>
